// Types for Admin -> Businesses -> Approvals (the reviewer's side of
// `pending_businesses`). The captain's own side of the same table lives in
// OnboardingObject.ts and is deliberately reused rather than duplicated here.

import { PendingBusiness } from "./OnboardingObject";

/**
 * One pending row as the ADMIN sees it: every captain's submissions, with the
 * review/audit columns the captain-facing projection omits.
 *
 * Extends PendingBusiness rather than restating it so the shared fields can
 * never drift, and so PendingBusinessCard-era helpers keep working on these
 * rows. Only the admin-only additions are declared below.
 */
export interface AdminPendingBusiness extends PendingBusiness {
  /** Submitting captain. The list response carries the id ONLY - no name - so
   *  any human-readable rendering has to resolve it (or the backend has to
   *  start sending captainName). */
  captainId: string;
  /** The nine_setup row this draft came from. Null on rows created before the
   *  setup id became required (observed on live PENDING rows). */
  nineSetupId: string | null;
  /** Schema version of the stored business payload. Surfaced so a future
   *  reviewer UI can refuse to render a payload shape it doesn't understand. */
  payloadVersion: number;
  /** Set only on REJECTED rows - the reason is written by the reviewer and is
   *  shown back to the captain. */
  rejectionReason: string | null;
  /** Reviewer's user id. Observed as null even on a freshly rejected row
   *  (2026-09-05), so treat it as best-effort audit data, not a guarantee. */
  reviewedBy: string | null;
  /** Set when the decision was recorded; null while PENDING. */
  reviewedAt: string | null;
}

/**
 * Wire values for `decision` on POST /api/v1/user/businesses/decision.
 *
 * IMPORTANT: this endpoint takes the STATUS vocabulary ("APPROVED"/"REJECTED"),
 * not the verb form ("APPROVE"/"REJECT"). The OpenAPI spec types the field as a
 * bare string with no enum, so this was confirmed against a live reject
 * (response came back approvalStatus: "REJECTED") rather than inferred. Never
 * inline these strings at a call site - enum spelling has silently broken
 * requests in this codebase before (see NineSetupApiService's CAPTAIN note).
 */
export const BusinessDecision = {
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
} as const;

export type BusinessDecision =
  (typeof BusinessDecision)[keyof typeof BusinessDecision];

/** UI-side input for a decision. `rejectionReason` is required by the UI for a
 *  REJECTED decision (the captain sees it) and omitted for APPROVED - the
 *  payload builder enforces that, not this type. */
export interface BusinessDecisionInput {
  pendingId: string;
  decision: BusinessDecision;
  rejectionReason?: string;
}
